import React, { useEffect, useMemo, useState } from 'react';
import { useSiteSettings } from './SiteSettingsContext';
import ExhibitionShowActions from './components/ExhibitionShowActions';
import { brandNameFromSettings } from './siteBrand';
import {
  exhibitionCountByCity,
  exhibitionItemsForCityChips,
  exhibitionItemsForCountryChips,
  exhibitionLiveStatus,
  exhibitionMatchesFilter,
  exhibitionStatusLabel,
  formatExhibitionDateRange,
  formatExhibitionWhereLine,
  formatScheduleDatePill,
  groupExhibitionsByMonth,
  normalizeLocationLabel,
  uniqueCitiesFromExhibitions,
  uniqueCountriesFromExhibitions,
} from './exhibitionsDisplay';
const PREVIEW_COUNT = 6;
const LARGE_LIST_THRESHOLD = 12;
const RECENT_INITIAL = 6;

function DatePill({ start, end, muted = false, className = '' }) {
  const pill = formatScheduleDatePill(start, end);
  const endAttr = end && end !== start ? end : undefined;

  return (
    <div className={`exhibition-date-pill${muted ? ' exhibition-date-pill--muted' : ''} ${className}`.trim()} aria-hidden="true">
      <time dateTime={start}>
        <span className="exhibition-date-pill__month">{pill.month}</span>
        <span className="exhibition-date-pill__day">{pill.day}</span>
        <span className="exhibition-date-pill__year">{pill.year}</span>
      </time>
      {endAttr ? <time dateTime={endAttr} className="visually-hidden" /> : null}
    </div>
  );
}

export function ExhibitionSpotlight({
  row,
  brandName,
  featured = false,
  badgeLabel = '',
  scheduleHref = '',
  scheduleLabel = '',
}) {
  if (!row) return null;

  const status = exhibitionLiveStatus(row);
  const statusLabel = exhibitionStatusLabel(status);
  const title = row.title?.trim();
  const where = formatExhibitionWhereLine(row);
  const when = formatExhibitionDateRange(row.event_start, row.event_end);
  const badge = String(badgeLabel || '').trim();
  const scheduleTo = String(scheduleHref || '').trim();
  const scheduleText = String(scheduleLabel || '').trim();
  const externalUrl = String(row?.external_url || '').trim();
  const showScheduleCta =
    featured &&
    scheduleTo &&
    scheduleText &&
    !(scheduleTo === '#exhibitions-upcoming' || scheduleTo === '#upcoming') &&
    !(externalUrl && (scheduleTo === externalUrl || scheduleText.toLowerCase().includes('event')));

  return (
    <article
      className={`exhibition-spotlight${featured ? ' exhibition-spotlight--featured' : ''}`}
      aria-label={title || 'Next exhibition'}
    >
      <div className="exhibition-spotlight__aside">
        {!featured && statusLabel ? (
          <span className={`exhibition-spotlight__status exhibition-spotlight__status--${status}`}>
            {statusLabel}
          </span>
        ) : null}
        <DatePill start={row.event_start} end={row.event_end} className="exhibition-spotlight__date" />
      </div>
      <div className="exhibition-spotlight__body">
        {featured && (badge || statusLabel) ? (
          <div className="exhibition-spotlight__labels">
            {badge ? <span className="exhibition-spotlight__badge">{badge}</span> : null}
            {statusLabel ? (
              <span className={`exhibition-spotlight__status exhibition-spotlight__status--${status}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
        ) : null}
        {title ? <h3 className="exhibition-spotlight__title">{title}</h3> : null}
        <p className="exhibition-spotlight__when">
          <time dateTime={row.event_start}>{when}</time>
        </p>
        {where ? (
          <p className="exhibition-spotlight__where">
            <svg className="exhibition-spotlight__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {where}
          </p>
        ) : null}
        {row.details ? <p className="exhibition-spotlight__details">{row.details}</p> : null}
        <ExhibitionShowActions row={row} brandName={brandName} compact={featured} />
      </div>
      {showScheduleCta ? (
        <div className="exhibition-spotlight__schedule">
          {/^https?:\/\//i.test(scheduleTo) ? (
            <a
              href={scheduleTo}
              className="exhibition-spotlight__schedule-cta btn btn-primary store-cta rounded-pill"
              target="_blank"
              rel="noopener noreferrer"
            >
              {scheduleText}
            </a>
          ) : (
            <a href={scheduleTo} className="exhibition-spotlight__schedule-cta btn btn-primary store-cta rounded-pill">
              {scheduleText}
            </a>
          )}
        </div>
      ) : null}
    </article>
  );
}

function ScheduleRow({ row, muted = false, brandName }) {
  const status = !muted ? exhibitionLiveStatus(row) : null;
  const statusLabel = exhibitionStatusLabel(status);
  const title = row.title?.trim();
  const where = formatExhibitionWhereLine(row);

  return (
    <li className={`exhibition-schedule-row${muted ? ' exhibition-schedule-row--muted' : ''}`}>
      <DatePill start={row.event_start} end={row.event_end} muted={muted} className="exhibition-schedule-row__date" />
      <div className="exhibition-schedule-row__body">
        <div className="exhibition-schedule-row__head">
          {title ? <h3 className="exhibition-schedule-row__title">{title}</h3> : null}
          {statusLabel ? (
            <span className={`exhibition-schedule-row__status exhibition-schedule-row__status--${status}`}>
              {statusLabel}
            </span>
          ) : null}
        </div>
        {where ? (
          <p className="exhibition-schedule-row__where">
            <svg className="exhibition-schedule-row__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {where}
          </p>
        ) : null}
        {row.details ? <p className="exhibition-schedule-row__details">{row.details}</p> : null}
        <ExhibitionShowActions row={row} brandName={brandName} compact showRsvp={!muted} />
      </div>
    </li>
  );
}

function GroupedScheduleList({ groups, muted = false, scrollable = false, brandName }) {
  if (!groups.length) {
    return <p className="exhibition-schedule-empty small text-muted mb-0 px-3 py-2">No shows match your filter.</p>;
  }

  return (
    <div className={`exhibition-schedule-list-wrap${scrollable ? ' exhibition-schedule-list-wrap--scroll' : ''}`}>
      {groups.map((group) => (
        <div key={group.key} className="exhibition-schedule-month">
          <h4 className="exhibition-schedule-month__label">{group.label}</h4>
          <ul className="exhibition-schedule-list exhibition-schedule-list--grid">
            {group.items.map((row) => (
              <ScheduleRow key={row.id} row={row} muted={muted} brandName={brandName} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button type="button" className={`exhibition-schedule-chip${active ? ' is-active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

function UpcomingSchedule({
  items,
  initialFullOpen = false,
  hideSpotlight = false,
  filterMinItems = 5,
  brandName,
  pageMode = false,
}) {
  const [fullOpen, setFullOpen] = useState(initialFullOpen || items.length <= LARGE_LIST_THRESHOLD);
  const [query, setQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const spotlight = items[0] || null;
  const scheduleItems = items.length > 1 ? items.slice(1) : [];

  const allCountries = useMemo(() => uniqueCountriesFromExhibitions(items), [items]);
  const countryCount = allCountries.length;

  const filterOpts = useMemo(
    () => ({ query, city: cityFilter, country: countryFilter }),
    [query, cityFilter, countryFilter],
  );

  const hasActiveFilter = Boolean(String(query || '').trim() || cityFilter || countryFilter);

  const cityChipItems = useMemo(
    () => exhibitionItemsForCityChips(items, { country: countryFilter, query }),
    [items, countryFilter, query],
  );
  const countryChipItems = useMemo(
    () => exhibitionItemsForCountryChips(items, { query }),
    [items, query],
  );

  const visibleCities = useMemo(() => uniqueCitiesFromExhibitions(cityChipItems), [cityChipItems]);
  const countriesForChips = useMemo(() => uniqueCountriesFromExhibitions(countryChipItems), [countryChipItems]);
  const visibleCountries = countriesForChips;
  const cityShowCounts = useMemo(() => exhibitionCountByCity(cityChipItems), [cityChipItems]);

  const filteredAll = useMemo(
    () => items.filter((row) => exhibitionMatchesFilter(row, filterOpts)),
    [items, filterOpts],
  );

  const filteredSchedule = useMemo(
    () => scheduleItems.filter((row) => exhibitionMatchesFilter(row, filterOpts)),
    [scheduleItems, filterOpts],
  );

  useEffect(() => {
    if (!cityFilter) return;
    const cityStillVisible = visibleCities.some(
      (city) => normalizeLocationLabel(city) === normalizeLocationLabel(cityFilter),
    );
    if (!cityStillVisible) setCityFilter('');
  }, [cityFilter, visibleCities]);

  useEffect(() => {
    if (!countryFilter) return;
    const countryStillValid = allCountries.some(
      (country) => normalizeLocationLabel(country) === normalizeLocationLabel(countryFilter),
    );
    if (!countryStillValid) setCountryFilter('');
  }, [countryFilter, allCountries]);

  const previewItems = useMemo(() => filteredSchedule.slice(0, PREVIEW_COUNT), [filteredSchedule]);
  const previewGroups = useMemo(() => groupExhibitionsByMonth(previewItems), [previewItems]);
  const fullGroups = useMemo(
    () => groupExhibitionsByMonth(hasActiveFilter ? filteredAll : filteredSchedule),
    [hasActiveFilter, filteredAll, filteredSchedule],
  );

  /** Search + location chips when there are enough shows to filter. */
  const showFilters = items.length >= filterMinItems;
  const useScroll = fullOpen && (hasActiveFilter ? filteredAll.length : filteredSchedule.length) > 8;
  const showSpotlight = !hasActiveFilter && spotlight && !hideSpotlight;

  const toggleCity = (city) => {
    if (cityFilter === city) {
      setCityFilter('');
      return;
    }
    setCityFilter(city);
    if (!countryFilter) {
      const match = items.find((row) => normalizeLocationLabel(row.city) === normalizeLocationLabel(city));
      if (match?.country) setCountryFilter(String(match.country).trim());
    }
  };

  const toggleCountry = (country) => {
    if (countryFilter === country) {
      setCountryFilter('');
      setCityFilter('');
      return;
    }
    setCountryFilter(country);
    const inCountry = items.filter(
      (row) => normalizeLocationLabel(row.country) === normalizeLocationLabel(country),
    );
    if (cityFilter) {
      const cityValid = inCountry.some(
        (row) => normalizeLocationLabel(row.city) === normalizeLocationLabel(cityFilter),
      );
      if (!cityValid) setCityFilter('');
    }
  };

  const clearLocationFilters = () => {
    setCityFilter('');
    setCountryFilter('');
  };

  const visibleCount = hasActiveFilter ? filteredAll.length : items.length;
  const listGroups = !fullOpen && !hasActiveFilter ? previewGroups : fullGroups;
  const hasListItems = hasActiveFilter ? filteredAll.length > 0 : scheduleItems.length > 0;
  const showCityChips =
    visibleCities.length > 1 || (Boolean(countryFilter) && visibleCities.length >= 1);
  const showCountryChips = visibleCountries.length > 1;
  const showLocationFilters = showCountryChips || showCityChips;

  return (
    <div
      className="exhibitions-panel exhibitions-panel--upcoming"
      id={pageMode ? 'exhibitions-upcoming' : undefined}
    >
      <div className="exhibitions-panel__head">
        <div className="exhibitions-panel__head-text">
          <h3 className="exhibitions-panel__heading">Upcoming &amp; current</h3>
          {!pageMode ? (
            <div className="exhibitions-panel__stats">
              <span className="exhibitions-stat-pill">
                {visibleCount} {visibleCount === 1 ? 'show' : 'shows'}
                {hasActiveFilter ? ` of ${items.length}` : ''}
              </span>
              {countryCount > 0 && (
                <span className="exhibitions-stat-pill exhibitions-stat-pill--muted">
                  {countryCount} {countryCount === 1 ? 'country' : 'countries'}
                </span>
              )}
            </div>
          ) : hasActiveFilter ? (
            <p className="exhibitions-panel__filter-note small text-muted mb-0">
              {visibleCount} matching {visibleCount === 1 ? 'show' : 'shows'}
            </p>
          ) : null}
        </div>
      </div>

      {showFilters && showLocationFilters ? (
        <div className="exhibitions-panel__filters">
          {showCountryChips ? (
            <div className="exhibition-filter-row">
              <span className="exhibition-filter-row__label">Region</span>
              <div className="exhibition-schedule-chips" role="group" aria-label="Filter by country">
                <FilterChip active={!countryFilter} onClick={clearLocationFilters}>
                  All regions
                </FilterChip>
                {visibleCountries.map((country) => (
                  <FilterChip
                    key={country}
                    active={countryFilter === country}
                    onClick={() => toggleCountry(country)}
                  >
                    {country}
                  </FilterChip>
                ))}
              </div>
            </div>
          ) : null}

          {showCityChips ? (
            <div className="exhibition-filter-row">
              <span className="exhibition-filter-row__label">City</span>
              <div className="exhibition-schedule-chips" role="group" aria-label="Filter by city">
                {(visibleCities.length > 1 || countryFilter) ? (
                  <FilterChip active={!cityFilter} onClick={() => setCityFilter('')}>
                    All cities
                    {countryFilter ? ` in ${countryFilter}` : ''}
                  </FilterChip>
                ) : null}
                {visibleCities.slice(0, 12).map((city) => {
                  const count = cityShowCounts.get(normalizeLocationLabel(city)) || 0;
                  return (
                    <FilterChip key={city} active={cityFilter === city} onClick={() => toggleCity(city)}>
                      {city}
                      {count > 1 ? ` (${count})` : ''}
                    </FilterChip>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="exhibitions-panel__content">
        {showSpotlight ? (
          <div className="exhibitions-panel__spotlight">
            <ExhibitionSpotlight row={spotlight} brandName={brandName} />
          </div>
        ) : null}

        {showFilters ? (
          <div className="exhibition-schedule-toolbar">
            <label className="exhibition-schedule-search">
              <span className="visually-hidden">Search exhibitions</span>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search city, venue, or show name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>
        ) : null}

        {!fullOpen && hasListItems ? (
          <>
            <GroupedScheduleList groups={listGroups} brandName={brandName} />
            {!hasActiveFilter && filteredSchedule.length > PREVIEW_COUNT ? (
              <button
                type="button"
                className="exhibition-schedule-more btn btn-link btn-sm"
                onClick={() => setFullOpen(true)}
              >
                View full schedule ({items.length} shows)
              </button>
            ) : null}
          </>
        ) : null}

        {fullOpen && hasListItems ? (
          <>
            <GroupedScheduleList groups={listGroups} scrollable={useScroll} brandName={brandName} />
            {items.length > LARGE_LIST_THRESHOLD ? (
              <button
                type="button"
                className="exhibition-schedule-more btn btn-link btn-sm"
                onClick={() => {
                  setFullOpen(false);
                  setQuery('');
                  clearLocationFilters();
                }}
              >
                Show less
              </button>
            ) : null}
          </>
        ) : null}

        {hasActiveFilter && filteredAll.length === 0 ? (
          <p className="exhibition-schedule-empty small text-muted mb-0">No shows match your filter.</p>
        ) : null}
      </div>
    </div>
  );
}

function RecentSchedule({ items, defaultExpanded = false, brandName, pageMode = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const visible = expanded ? items : items.slice(0, RECENT_INITIAL);
  const groups = useMemo(() => groupExhibitionsByMonth(visible), [visible]);
  const hidden = items.length - RECENT_INITIAL;

  return (
    <details
      className="exhibitions-panel exhibitions-panel--recent"
      id={pageMode ? 'exhibitions-past' : undefined}
      open={defaultExpanded || undefined}
    >
      <summary className="exhibitions-panel__summary">
        <span className="exhibitions-panel__summary-text">Past exhibitions</span>
        <span className="exhibitions-panel__count">{items.length}</span>
      </summary>
      <div className="exhibitions-panel__body">
        <GroupedScheduleList groups={groups} muted scrollable={expanded && items.length > 8} brandName={brandName} />
        {hidden > 0 && !expanded && (
          <button type="button" className="exhibition-schedule-more btn btn-link btn-sm" onClick={() => setExpanded(true)}>
            Show {hidden} more past {hidden === 1 ? 'show' : 'shows'}
          </button>
        )}
        {expanded && hidden > 0 && (
          <button type="button" className="exhibition-schedule-more btn btn-link btn-sm" onClick={() => setExpanded(false)}>
            Show fewer
          </button>
        )}
      </div>
    </details>
  );
}

export default function HomeExhibitions({
  upcoming = [],
  recent = [],
  status = 'loading',
  showHeader = true,
  initialFullOpen = false,
  pageMode = false,
  className = '',
}) {
  const { settings: site } = useSiteSettings();
  const home = site.page_copy?.home || {};
  const brandName = brandNameFromSettings(site);

  if (status !== 'ready') return null;
  if (upcoming.length + recent.length === 0) return null;

  return (
    <section
      id="exhibitions-shows"
      className={`exhibitions-section ${className}`.trim()}
      aria-labelledby={showHeader ? 'exhibitions-heading' : undefined}
    >
      <div className="exhibitions-section__container">
        {showHeader ? (
          <header className="exhibitions-section__header">
            {home.exhibitions_eyebrow ? <p className="exhibitions-eyebrow">{home.exhibitions_eyebrow}</p> : null}
            <h2 id="exhibitions-heading" className="exhibitions-section__title">
              {home.exhibitions_title}
            </h2>
            {home.exhibitions_subtitle ? (
              <p className="exhibitions-section__subtitle">{home.exhibitions_subtitle}</p>
            ) : null}
          </header>
        ) : null}

        {upcoming.length > 0 && (
          <UpcomingSchedule
            items={upcoming}
            initialFullOpen={initialFullOpen || pageMode}
            hideSpotlight={pageMode}
            filterMinItems={pageMode ? 2 : 5}
            brandName={brandName}
            pageMode={pageMode}
          />
        )}
        {recent.length > 0 && (
          <RecentSchedule items={recent} defaultExpanded={pageMode} brandName={brandName} pageMode={pageMode} />
        )}
      </div>
    </section>
  );
}
